import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip, Alert, Typography,
  Snackbar, Tooltip
} from '@mui/material';
import { Add, Delete, Edit, Person } from '@mui/icons-material';
import api from '../utils/api';

interface Role {
  id: number;
  code: string;
  name: string;
  description?: string;
}

interface Guide {
  id: number;
  name: string;
  is_active: number | boolean;
}

interface UserRow {
  id: number;
  username: string;
  role: string;
  guide_id: number | null;
  guide_name: string | null;
  is_active: number | boolean;
}

const ROLE_COLORS: Record<string, 'error' | 'primary' | 'success' | 'default'> = {
  admin: 'error',
  manager: 'primary',
  guide: 'success',
};

const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<string>('');

  // Создание
  const [openCreate, setOpenCreate] = useState(false);
  const [createData, setCreateData] = useState({ username: '', password: '', role: 'manager', guide_id: '' as number | '' });
  const [createError, setCreateError] = useState('');

  // Редактирование
  const [openEdit, setOpenEdit] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [editData, setEditData] = useState({ role: '', guide_id: '' as number | '', password: '' });
  const [editError, setEditError] = useState('');

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    // Получаем роль текущего пользователя из токена
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserRole(payload.role || '');
      } catch (e) {
        console.error('Ошибка парсинга токена');
      }
    }

    fetchUsers();
    fetchRoles();
    fetchGuides();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchGuides = async () => {
    try {
      const res = await api.get('/guides');
      if (Array.isArray(res.data)) {
        setGuides(res.data.filter((g: Guide) => g.is_active === 1 || g.is_active === true));
      }
    } catch (err) { console.error(err); }
  };

  const roleName = (code: string) => roles.find(r => r.code === code)?.name || code;

  const handleCreate = async () => {
    setCreateError('');
    if (!createData.username || !createData.password) {
      setCreateError('Заполните логин и пароль');
      return;
    }
    try {
      setSaving(true);
      await api.post('/users', {
        username: createData.username,
        password: createData.password,
        role: createData.role,
        guide_id: createData.guide_id === '' ? null : createData.guide_id,
      });
      setOpenCreate(false);
      setCreateData({ username: '', password: '', role: 'manager', guide_id: '' });
      setSnackbar({ open: true, message: 'Пользователь создан', severity: 'success' });
      fetchUsers();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (u: UserRow) => {
    setEditing(u);
    setEditData({
      role: u.role,
      guide_id: u.guide_id ?? '',
      password: '',
    });
    setEditError('');
    setOpenEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setEditError('');
    try {
      setSaving(true);
      const payload: any = {
        role: editData.role,
        guide_id: editData.guide_id === '' ? null : editData.guide_id,
      };
      if (editData.password) payload.password = editData.password;

      await api.put(`/users/${editing.id}`, payload);
      setOpenEdit(false);
      setSnackbar({ open: true, message: 'Пользователь обновлён', severity: 'success' });
      fetchUsers();
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить пользователя?')) return;
    try {
      await api.delete(`/users/${id}`);
      setSnackbar({ open: true, message: 'Пользователь удалён', severity: 'success' });
      fetchUsers();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Ошибка удаления', severity: 'error' });
    }
  };

  // Проверка, может ли текущий пользователь редактировать конкретную строку
  // (admin может редактировать всех, кроме себя; остальные не могут редактировать admin)
  const canEdit = (u: UserRow) => {
    if (u.username === 'admin') {
      return currentUserRole === 'admin'; // Только админ может редактировать admin
    }
    return true; // Обычных пользователей может редактировать любой
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Управление доступом</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenCreate(true)}>
          Добавить пользователя
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell>Логин</TableCell>
              <TableCell>Роль</TableCell>
              <TableCell>Привязка к гиду</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" color="action" /> {u.username}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={roleName(u.role)}
                    color={ROLE_COLORS[u.role] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{u.guide_name || '-'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={canEdit(u) ? 'Редактировать' : 'Недоступно'}>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEdit(u)}
                        disabled={!canEdit(u)}
                      >
                        <Edit />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={u.username === 'admin' ? 'Недоступно для admin' : 'Удалить'}>
                    <span>
                      <IconButton color="error" onClick={() => handleDelete(u.id)} disabled={u.username === 'admin'}>
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Новый пользователь</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Логин" fullWidth value={createData.username} onChange={e => setCreateData({ ...createData, username: e.target.value })} />
            <TextField label="Пароль" type="password" fullWidth value={createData.password} onChange={e => setCreateData({ ...createData, password: e.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select value={createData.role} label="Роль" onChange={e => setCreateData({ ...createData, role: e.target.value })}>
                {roles.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Привязка к гиду</InputLabel>
              <Select
                value={createData.guide_id}
                label="Привязка к гиду"
                onChange={e => setCreateData({ ...createData, guide_id: e.target.value as number | '' })}
              >
                <MenuItem value=""><em>Не привязан</em></MenuItem>
                {guides.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Отмена</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving}>Создать</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог редактирования */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Редактирование: {editing?.username}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select value={editData.role} label="Роль" onChange={e => setEditData({ ...editData, role: e.target.value })} disabled={editing?.username === 'admin'}>
                {roles.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Привязка к гиду</InputLabel>
              <Select
                value={editData.guide_id}
                label="Привязка к гиду"
                onChange={e => setEditData({ ...editData, guide_id: e.target.value as number | '' })}
              >
                <MenuItem value=""><em>Не привязан</em></MenuItem>
                {guides.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Новый пароль"
              type="password"
              fullWidth
              value={editData.password}
              onChange={e => setEditData({ ...editData, password: e.target.value })}
              helperText="Оставьте пустым, чтобы не менять"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Отмена</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>Сохранить</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ bottom: 24, left: 24, maxWidth: 400 }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 1, width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersTab;
